import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    CircularProgress,
    Chip,
    Stack,
    Divider,
    Alert,
    alpha
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { createCheckoutSession, getSubscription, changePlan, BillingInterval, SubscriptionInfo } from '../../services/payment-service';
import { useUser } from '../../hooks/use-user';
import MainLayout from '@/layouts/MainLayout';
import { brand } from '../../shared-theme/themePrimitives';

const TIER_RANK: Record<string, number> = { BASIC: 0, PRO: 1, ENTERPRISE: 2 };

const PricingPage = () => {
    const { user } = useUser();
    const [loading, setLoading] = useState<string | null>(null);
    const [billingInterval, setBillingInterval] = useState<BillingInterval>('MONTHLY');
    const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
    const [subLoading, setSubLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        getSubscription()
            .then(setSubscription)
            .catch(console.error)
            .finally(() => setSubLoading(false));
    }, []);

    const currentTier = user?.subscriptionTier || 'BASIC';
    const currentInterval = subscription?.billingInterval || 'MONTHLY';
    const isCancelled = subscription?.status === 'CANCELLED';

    const handleAction = async (tierKey: string) => {
        setLoading(tierKey);
        setError(null);
        try {
            if (currentTier === 'BASIC' || isCancelled) {
                // New subscription or re-subscribe after cancel
                const checkoutUrl = await createCheckoutSession(tierKey as 'PRO' | 'ENTERPRISE', billingInterval);
                window.location.href = checkoutUrl;
            } else {
                // Plan change (upgrade/downgrade/interval switch)
                const result = await changePlan(tierKey as 'PRO' | 'ENTERPRISE' | 'BASIC', billingInterval);
                if (result.checkoutUrl) {
                    window.location.href = result.checkoutUrl;
                } else {
                    // Reload subscription data
                    const updated = await getSubscription();
                    setSubscription(updated);
                }
            }
        } catch (err: any) {
            const message = err?.response?.data?.error || 'Failed to process plan change. Please try again.';
            setError(message);
            console.error('Failed to process plan change', err);
        } finally {
            setLoading(null);
        }
    };

    const getButtonConfig = (tierKey: string) => {
        const isCurrentTier = currentTier === tierKey;
        const isCurrentInterval = currentInterval === billingInterval;
        const tierRank = TIER_RANK[tierKey];
        const currentRank = TIER_RANK[currentTier];

        // Current plan, same interval, not cancelled
        if (isCurrentTier && isCurrentInterval && !isCancelled) {
            return { label: 'Current Plan', variant: 'outlined' as const, disabled: true, color: 'primary' as const };
        }

        // Same tier, different interval
        if (isCurrentTier && !isCurrentInterval && !isCancelled) {
            return {
                label: `Switch to ${billingInterval === 'ANNUAL' ? 'Annual' : 'Monthly'}`,
                variant: 'contained' as const,
                disabled: false,
                color: 'primary' as const
            };
        }

        // Cancelled — show re-subscribe
        if (isCancelled && tierKey !== 'BASIC') {
            return { label: 'Subscribe', variant: 'contained' as const, disabled: false, color: 'primary' as const };
        }

        // Basic tier card — no action needed
        if (tierKey === 'BASIC') {
            if (currentTier === 'BASIC') {
                return { label: 'Current Plan', variant: 'outlined' as const, disabled: true, color: 'primary' as const };
            }
            return { label: 'Downgrade', variant: 'outlined' as const, disabled: true, color: 'inherit' as const };
        }

        // Upgrade
        if (tierRank > currentRank) {
            return { label: 'Upgrade', variant: 'contained' as const, disabled: false, color: 'primary' as const };
        }

        // Downgrade
        return { label: 'Downgrade', variant: 'outlined' as const, disabled: false, color: 'inherit' as const };
    };

    const tiers = [
        {
            title: 'Basic',
            subtitle: 'For getting started',
            monthlyPrice: 0,
            annualPrice: 0,
            features: ['20 email reveals / month', 'Basic search', 'Email support'],
            tierKey: 'BASIC',
            popular: false,
        },
        {
            title: 'Pro',
            subtitle: 'For growing teams',
            monthlyPrice: 25,
            annualPrice: 250,
            features: ['200 email reveals / month', 'Advanced search & filters', 'Priority support'],
            tierKey: 'PRO',
            popular: true,
        },
        {
            title: 'Enterprise',
            subtitle: 'For power users',
            monthlyPrice: 200,
            annualPrice: 2000,
            features: ['1,000 email reveals / month', 'Advanced search & filters', 'Dedicated account manager', 'Priority support'],
            tierKey: 'ENTERPRISE',
            popular: false,
        }
    ];

    if (subLoading) {
        return (
            <MainLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <CircularProgress />
                </Box>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <Box sx={{ maxWidth: 1100, mx: 'auto', px: 3, py: 6 }}>
                {/* Header */}
                <Stack spacing={1.5} alignItems="center" textAlign="center" sx={{ mb: 5 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em', color: '#0F172A' }}>
                        Plans & Pricing
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500 }}>
                        Choose the plan that fits your outreach needs. Upgrade or downgrade anytime.
                    </Typography>
                </Stack>

                {/* Billing toggle */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 5 }}>
                    <Box sx={{
                        bgcolor: (theme) => alpha(theme.palette.background.default, 0.6),
                        p: 0.5,
                        borderRadius: '14px',
                        border: '1px solid',
                        borderColor: alpha('#000', 0.06),
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}>
                        <Stack direction="row" spacing={0.5}>
                            {(['MONTHLY', 'ANNUAL'] as const).map((cycle) => (
                                <Box
                                    key={cycle}
                                    onClick={() => setBillingInterval(cycle)}
                                    sx={{
                                        px: 3, py: 1, borderRadius: '10px', cursor: 'pointer',
                                        bgcolor: billingInterval === cycle ? '#0F172A' : 'transparent',
                                        color: billingInterval === cycle ? 'white' : '#94A3B8',
                                        fontWeight: 600, fontSize: '0.875rem',
                                        transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                                        userSelect: 'none',
                                        display: 'flex', alignItems: 'center', gap: 1,
                                    }}
                                >
                                    {cycle === 'MONTHLY' ? 'Monthly' : 'Annual'}
                                    {cycle === 'ANNUAL' && (
                                        <Box component="span" sx={{
                                            fontSize: '0.65rem', fontWeight: 700,
                                            bgcolor: billingInterval === 'ANNUAL' ? alpha('#fff', 0.2) : '#DCFCE7',
                                            color: billingInterval === 'ANNUAL' ? 'white' : '#16A34A',
                                            px: 1, py: 0.25, borderRadius: '6px',
                                        }}>
                                            Save 17%
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                </Box>

                {/* Cancelled banner */}
                {isCancelled && (
                    <Box sx={{
                        textAlign: 'center',
                        mb: 4, p: 2,
                        borderRadius: '14px',
                        backgroundColor: '#FFF7ED',
                        border: '1px solid #FFEDD5',
                    }}>
                        <Typography variant="body2" sx={{ color: '#C2410C', fontWeight: 500 }}>
                            Your {currentTier} subscription has been cancelled and will expire at the end of the billing period.
                            You can re-subscribe to any plan below.
                        </Typography>
                    </Box>
                )}

                {/* Error banner */}
                {error && (
                    <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 4, borderRadius: '14px' }}>
                        {error}
                    </Alert>
                )}

                {/* Plan cards */}
                <Grid container spacing={3} justifyContent="center" alignItems="stretch">
                    {tiers.map((tier) => {
                        const isCurrentPlan = currentTier === tier.tierKey && !isCancelled;
                        const btnConfig = getButtonConfig(tier.tierKey);
                        const price = billingInterval === 'ANNUAL' ? tier.annualPrice : tier.monthlyPrice;
                        const isPopular = tier.popular;
                        const isHighlight = isPopular || isCurrentPlan;

                        return (
                            <Grid size={{ xs: 12, md: 4 }} key={tier.tierKey}>
                                <Box sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column',
                                    p: 4,
                                    bgcolor: isHighlight ? alpha(brand[400], 0.03) : 'white',
                                    borderRadius: '24px',
                                    border: isHighlight ? '2px solid' : '1px solid',
                                    borderColor: isCurrentPlan
                                        ? brand[400]
                                        : isPopular
                                            ? alpha(brand[400], 0.4)
                                            : alpha('#000', 0.04),
                                    boxShadow: isHighlight
                                        ? `0 24px 48px -12px ${alpha(brand[400], 0.15)}, 0 0 0 1px ${alpha(brand[400], 0.1)}`
                                        : '0 1px 3px rgba(0,0,0,0.04)',
                                    position: 'relative',
                                    overflow: 'visible',
                                    transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                                    '&:hover': {
                                        transform: 'translateY(-6px)',
                                        boxShadow: isHighlight
                                            ? `0 32px 64px -16px ${alpha(brand[400], 0.2)}`
                                            : '0 24px 48px -12px rgba(0,0,0,0.08)',
                                    },
                                }}>
                                    {/* Badges — positioned on top edge */}
                                    {isPopular && !isCurrentPlan && (
                                        <Chip
                                            icon={<StarRoundedIcon sx={{ fontSize: 14, color: 'white !important' }} />}
                                            label="Most Popular"
                                            size="small"
                                            sx={{
                                                position: 'absolute', top: -14, left: '50%',
                                                transform: 'translateX(-50%)',
                                                bgcolor: '#0F172A', color: 'white',
                                                fontWeight: 700, fontSize: '0.75rem', height: 28,
                                                boxShadow: '0 4px 12px rgba(15,23,42,0.25)',
                                                '& .MuiChip-label': { color: 'white' },
                                                '& .MuiChip-icon': { color: 'white' },
                                            }}
                                        />
                                    )}
                                    {isCurrentPlan && (
                                        <Chip
                                            label="Your Plan"
                                            size="small"
                                            sx={{
                                                position: 'absolute', top: -14, left: '50%',
                                                transform: 'translateX(-50%)',
                                                bgcolor: brand[400], color: 'white',
                                                fontWeight: 700, fontSize: '0.75rem', height: 28,
                                                boxShadow: `0 4px 12px ${alpha(brand[400], 0.3)}`,
                                                '& .MuiChip-label': { color: 'white' },
                                            }}
                                        />
                                    )}

                                    {/* Plan name */}
                                    <Typography variant="h5" fontWeight={700} sx={{
                                        mb: 0.5, textAlign: 'center', color: '#1E293B',
                                        mt: (isPopular || isCurrentPlan) ? 2 : 0,
                                    }}>
                                        {tier.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                                        {tier.subtitle}
                                    </Typography>

                                    {/* Price */}
                                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                                        {price === 0 ? (
                                            <Typography variant="h3" fontWeight={800} sx={{ color: '#0F172A' }}>Free</Typography>
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center' }}>
                                                <Typography variant="h3" fontWeight={800} sx={{ color: '#0F172A' }}>${price}</Typography>
                                                <Typography variant="body1" color="text.secondary" sx={{ ml: 0.5 }}>
                                                    /{billingInterval === 'ANNUAL' ? 'yr' : 'mo'}
                                                </Typography>
                                            </Box>
                                        )}
                                        {billingInterval === 'ANNUAL' && tier.monthlyPrice > 0 && (
                                            <Typography variant="caption" color="text.secondary">
                                                ${(tier.annualPrice / 12).toFixed(2)}/mo billed annually
                                            </Typography>
                                        )}
                                    </Box>

                                    <Divider sx={{ mb: 3 }} />

                                    {/* Features */}
                                    <Stack spacing={2} sx={{ mb: 4, flexGrow: 1 }}>
                                        {tier.features.map((feature) => (
                                            <Stack key={feature} direction="row" spacing={1.5} alignItems="center">
                                                <Box sx={{
                                                    width: 20, height: 20, borderRadius: '6px',
                                                    bgcolor: alpha(isHighlight ? brand[400] : '#94A3B8', 0.1),
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}>
                                                    <CheckRoundedIcon sx={{ fontSize: 14, color: isHighlight ? brand[400] : '#94A3B8' }} />
                                                </Box>
                                                <Typography variant="body2" color="text.secondary">{feature}</Typography>
                                            </Stack>
                                        ))}
                                    </Stack>

                                    {/* Action button */}
                                    <Button
                                        fullWidth
                                        variant={btnConfig.disabled ? 'outlined' : isHighlight ? 'contained' : 'outlined'}
                                        size="large"
                                        disabled={btnConfig.disabled || loading === tier.tierKey}
                                        onClick={() => handleAction(tier.tierKey)}
                                        sx={{
                                            mt: 'auto', py: 1.5, borderRadius: '14px', fontWeight: 700,
                                            textTransform: 'none', fontSize: '0.95rem',
                                            transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                                            ...((!btnConfig.disabled && isHighlight)
                                                ? {
                                                    bgcolor: '#0F172A', color: 'white',
                                                    boxShadow: '0 4px 12px rgba(15,23,42,0.2)',
                                                    '&:hover': { bgcolor: '#1E293B', transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(15,23,42,0.25)' },
                                                }
                                                : {
                                                    borderColor: '#E2E8F0', borderWidth: '1.5px', color: '#475569',
                                                    '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC', transform: 'translateY(-1px)' },
                                                }),
                                        }}
                                    >
                                        {loading === tier.tierKey ? (
                                            <CircularProgress size={22} color="inherit" />
                                        ) : (
                                            btnConfig.label
                                        )}
                                    </Button>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* Footer note */}
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 5 }}>
                    All plans include a rolling monthly credit reset. Cancel or change plans anytime.
                </Typography>
            </Box>
        </MainLayout>
    );
};

export default PricingPage;
